import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getRateDashboardThunk,
  morningCheckThunk,
  setSelectedBranch,
} from "../../../store/thunks/rateThunks";
import { setActiveTab } from "../../../store/slices/rateSlice";
import MorningRateCard from "../../../components/Rates/MorningRateCard";
import MetalRateCards from "../../../components/Rates/MetalRateCards";
import BranchRateOverview from "../../../components/Rates/BranchRateOverview";
import MarketComparison from "../../../components/Rates/MarketComparison";
import QuickActions from "../../../components/Rates/QuickActions";
import BranchSelector from "../../../components/UI/BranchSelector";

const RateDashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    dashboard,
    loading,
    dailyRates,
    ui: { selectedBranch },
  } = useSelector((state) => state.rate);

  const [showMorningAlert, setShowMorningAlert] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(
    selectedBranch || user?.branchAccess?.primaryBranch || ""
  );
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (user?.branchAccess?.primaryBranch && !currentBranch) {
      const primaryBranch = user.branchAccess.primaryBranch;
      setCurrentBranch(primaryBranch);
      dispatch(setSelectedBranch(primaryBranch));
    }
  }, [user, currentBranch, dispatch]);

  useEffect(() => {
    if (currentBranch) {
      dispatch(getRateDashboardThunk({ branchCode: currentBranch }));
    }

    const checkMorning = async () => {
      const result = await dispatch(
        morningCheckThunk({ branchCode: currentBranch })
      ).unwrap();
      if (result.data.status === "NO_RATE") {
        setShowMorningAlert(true);
      }
    };

    const now = new Date();
    if (now.getHours() === 9 && currentBranch) {
      checkMorning();
    }
  }, [dispatch, currentBranch]);

  const handleBranchChange = (branchCode) => {
    setCurrentBranch(branchCode);
    dispatch(setSelectedBranch(branchCode));
  };

  return (
    <div className="space-y-4 sm:space-y-6 pb-20 px-3 sm:px-4 lg:px-6">
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Rate Dashboard</h1>
            <p className="text-gray-600 text-xs sm:text-base mt-1">
              Monitor and manage metal rates across branches
            </p>
          </div>

          {/* Mobile: Compact selector, Desktop: Full selector */}
          <BranchSelector
            selectedBranch={currentBranch}
            onBranchChange={handleBranchChange}
            compact={isMobile}
          />
        </div>
      </div>

      {showMorningAlert && (
        <MorningRateCard
          onSetRates={() => {
            dispatch(setActiveTab("daily"));
            window.location.href = "/rates/daily";
          }}
          onDismiss={() => setShowMorningAlert(false)}
        />
      )}

      {currentBranch ? (
        <>
          {/* MetalRateCards - Responsive grid handled within component */}
          <MetalRateCards
            rates={
              dashboard.metalRates?.[currentBranch] || dashboard.metalRates
            }
            loading={loading.dashboard}
          />

          <MarketComparison branchCode={currentBranch} />

          {/* QuickActions sticky bar */}
          <QuickActions isSticky={true} />
        </>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 text-center">
          <p className="text-gray-500 text-sm sm:text-base">
            Please select a branch to view the dashboard
          </p>
        </div>
      )}
    </div>
  );
};

export default RateDashboard;
